import { memo, useEffect, useState } from 'react';

import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Popper from 'components/global/Popper';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';

import { faAngleDown, faCopy, faPaste } from '@fortawesome/pro-regular-svg-icons';

import * as S from './DynamicListMenu.styled';

/**
 * @author Burak Unsal
 */
const DynamicListMenu = (props) => {
	const { menuItems, row, onClose, copyValue, copyColumnValues, isRowMenu } = props;

	const [selectedMenuIndex, setSelectedMenuIndex] = useState(null);

	const [headerItems, setHeaderItems] = useState(null);
	const [footerItems, setFooterItems] = useState(null);
	const [filteredMenus, setFilteredMenus] = useState(null);

	useEffect(() => {
		const tempHeaderItems = [];
		const tempFooterItems = [];
		const tempFilteredMenus = [];

		menuItems?.forEach((menu) => {
			const tempMenu = { ...menu, items: [] };
			menu.items?.forEach((item) => {
				if (item.headerItem) {
					tempHeaderItems.push(item);
				} else if (item.footerItem) {
					tempFooterItems.push(item);
				} else {
					tempMenu.items.push(item);
				}
			});
			if (tempMenu.items.length > 0) {
				tempFilteredMenus.push(tempMenu);
			}
		});

		setHeaderItems(tempHeaderItems);
		setFooterItems(tempFooterItems);
		setFilteredMenus(tempFilteredMenus);
	}, [menuItems]);

	const { showInfo } = useAlert();

	const handleClick = (e, isDisabled, action) => {
		e.preventDefault();
		e.stopPropagation();
		if (isDisabled) return;
		if (!action) {
			showInfo({ header: 'alert.underDevelopment' });
			return;
		}
		action(row);
		onClose();
	};

	const handleCopy = () => {
		navigator.clipboard.writeText(copyValue);
		showInfo({
			header: 'copiedToClipboard'
		});
		onClose();
	};

	const handleCopyColumn = () => {
		navigator.clipboard.writeText(copyColumnValues.join('\n'));
		showInfo({
			header: 'columnCopiedToClipboard'
		});
		onClose();
	};

	const showHeader = copyValue || copyColumnValues || headerItems?.length > 0;

	return (
		<S.MenuContainer className={`${isRowMenu ? 'row-menu' : ''}`}>
			<S.Menu id="menuContainer">
				{showHeader && (
					<S.HeaderMenu>
						{copyValue && (
							<Button variant="blackOutline" label="copy" icon={faCopy} onClick={handleCopy} fullWidth />
						)}
						{copyColumnValues && (
							<Button
								variant="blackOutline"
								label="copyColumn"
								icon={faPaste}
								onClick={handleCopyColumn}
								fullWidth
							/>
						)}
						{headerItems?.map((item, index) => {
							if (item.render && item.render(row) === false) return;

							const isDisabled = item.disabled ? item.disabled(row) : false;

							return (
								<Button
									key={index}
									variant="blackOutline"
									label={item.label}
									icon={item.icon}
									onContextMenu={(e) => handleClick(e, isDisabled, item.action)}
									onClick={(e) => handleClick(e, isDisabled, item.action)}
									disabled={isDisabled}
									disabledTooltip={isDisabled}
									fullWidth
								/>
							);
						})}
					</S.HeaderMenu>
				)}
				{filteredMenus?.map((menu, index) => (
					<MenuButton
						key={menu.label ?? index}
						index={index}
						menu={menu}
						row={row}
						handleClick={handleClick}
						selectedMenuIndex={selectedMenuIndex}
						setSelectedMenuIndex={setSelectedMenuIndex}
					/>
				))}
				{footerItems?.length > 0 && filteredMenus?.length > 0 && <S.Line />}
				{footerItems?.map((item, index) => (
					<MenuItem key={item.label ?? index} index={index} item={item} row={row} handleClick={handleClick} />
				))}
			</S.Menu>
		</S.MenuContainer>
	);
};

const MenuButton = (props) => {
	const { menu, index, row, handleClick, selectedMenuIndex, setSelectedMenuIndex } = props;

	const [buttonRef, setButtonRef] = useState(null);

	return (
		<>
			<S.StyledMenuButton
				ref={setButtonRef}
				id={menu.label ? `action_${menu.label}` : `action_${index}`}
				className={`menu-item ${selectedMenuIndex === index ? 'rotate' : ''} ${menu.className}`}
				variant={menu.variant ?? 'transparent'}
				onMouseEnter={() => setSelectedMenuIndex(index)}
				fullWidth>
				<S.MenuButtonContent>
					{menu.icon && (
						<Icon icon={menu.icon} color={menu.variant ? 'var(--alwaysWhiteColor)' : 'var(--black)'} />
					)}
					<Typography color={menu.variant ? 'var(--alwaysWhiteColor)' : 'var(--black)'}>
						{menu.label}
					</Typography>
				</S.MenuButtonContent>

				<Icon
					fontSize="var(--paragraph3-font-size)"
					icon={faAngleDown}
					color={menu.variant ? 'var(--alwaysWhiteColor)' : 'var(--black)'}
				/>
			</S.StyledMenuButton>
			{selectedMenuIndex === index && buttonRef && (
				<Popper refElement={buttonRef} placement="right-start" offsetY={-8} alwaysShow>
					{() => (
						<S.PopperContainer>
							<S.Menu id="menuContainer">
								{menu?.items?.map((item, index) => (
									<MenuItem
										key={item.label ?? index}
										index={index}
										item={item}
										row={row}
										handleClick={handleClick}
									/>
								))}
							</S.Menu>
						</S.PopperContainer>
					)}
				</Popper>
			)}
		</>
	);
};

const MenuItem = (props) => {
	const { item, row, index, handleClick } = props;
	const isMobile = useMediaQuery(Breakpoints.mobile);

	if (item.render && item.render(row) === false) return;

	const isDisabled = item.disabled ? item.disabled(row) : false;

	return (
		<Tooltip title={isDisabled} placement={isMobile ? 'top' : 'right'} key={index}>
			<S.MenuItem
				id={item.label ? `action_${item.label}` : `action_${index}`}
				key={index}
				className={`${item.className} ${isDisabled ? 'disabled' : ''}`}
				onMouseDown={(e) => handleClick(e, isDisabled, item.action)}
				onTouchEnd={(e) => handleClick(e, isDisabled, item.action)}
				variant="transparent">
				{item.icon && <Icon icon={item.icon} />}
				<Typography>{item.label}</Typography>
			</S.MenuItem>
		</Tooltip>
	);
};

export default memo(DynamicListMenu);
